import React from 'react';

export function createContext<ContextValueType extends object | null>({
  rootComponentName,
  defaultContext,
}: {
  rootComponentName: string;
  defaultContext?: ContextValueType;
}) {
  const Context = React.createContext<ContextValueType | undefined>(
    defaultContext,
  );

  function Provider(props: ContextValueType & { children: React.ReactNode }) {
    const { children, ...context } = props;
    const value = React.useMemo(() => context, [context]) as ContextValueType;
    return <Context.Provider value={value}>{children}</Context.Provider>;
  }

  // Overloads for the useContext function
  function useContext(): ContextValueType | undefined;
  function useContext(mustExist: false): ContextValueType | undefined;
  function useContext(mustExist: true): ContextValueType;
  function useContext(mustExist = false): ContextValueType | undefined {
    const context = React.useContext(Context);
    if (context) return context;
    if (defaultContext !== undefined) return defaultContext;

    if (mustExist) {
      throw new Error(
        `\`${rootComponentName}\` must be used within a \`${Provider.displayName}\` component.`,
      );
    }

    return undefined;
  }

  Provider.displayName = rootComponentName + 'Provider';
  return [Provider, useContext] as const;
}
